import { useInternalState, assignMswData, serializeQuery } from '../hooks/internalState';
import {
  PostAlbumVideoCommentRepliesResult,
  PostAlbumVideoCommentRepliesOptions,
  PostAlbumVideoCommentRepliesFields,
  postAlbumVideoCommentReplies,
} from '../fetchers/album/video/comment/replies';
import { useCallback } from 'react';
import { useSWRConfig } from 'swr';
import { useGctlConfig } from '../hooks/GctlProvider';

/**
 * DO NOT MODIFY
 * Auto-generated by gctl-plugin-vimeo-hooks
 */

type UsePostAlbumVideoCommentRepliesOptions<Fields extends PostAlbumVideoCommentRepliesFields> =
  PostAlbumVideoCommentRepliesOptions<Fields>;

/**
 * Add a reply to a video comment
 * This method adds a reply to the specified video comment.
 */

export function usePostAlbumVideoCommentReplies<
  Fields extends PostAlbumVideoCommentRepliesFields,
>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<
    PostAlbumVideoCommentRepliesResult<Fields> | undefined
  >();

  let call = useCallback(
    async (options: UsePostAlbumVideoCommentRepliesOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/albums/${options.where.albumId}/videos/${options.where.videoId}/comments/${
            options.where.commentId
          }/replies${serializeQuery(options)}`,
          postAlbumVideoCommentReplies({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
          false,
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(usePostAlbumVideoCommentReplies, {
    endpoint: '/albums/:albumId/videos/:videoId/comments/:commentId/replies',
    method: 'POST',
  });
}
